import { Button, Col, Divider, Modal, Row, Select, Spin, Tag, Typography } from 'antd';
import { IAtribuicaoItem, IReceitaDetalhe, STATUS_TAG_COLOR } from '../../interface';
import { convertDataUSParaBrasilEHora } from '../../../../services/Conversores';
import { useEffect, useState } from 'react';
import { useGrupoContext } from '../../context/GroupContext';
import { ReceitaType } from '../../hooks/useReceitas';
import { stateGlobal } from '../../../../interfaces/Global';
import { useSelector } from 'react-redux';
import { useGrupo } from '../../hooks/useGrupos';
import { ItemSI } from './components/ItemSI';

import './styles.css';

const { Text, Title } = Typography;

interface IModalProps {
    visible: boolean;
    data: any;
    onClose: () => void;
    onSave: (values: any) => void;
    loading?: boolean;
    grupoHook: ReturnType<typeof useGrupo>;
    getReceitaByCodigo?: (codigo: string) => Promise<IReceitaDetalhe | null>;
}

export const ModalReceita = ({
    visible,
    data,
    onClose,
    onSave,
    loading = false,
    grupoHook,
    getReceitaByCodigo,
}: IModalProps) => {
    const propsStateTheme = useSelector((state: stateGlobal) => state.global);
    const isLight = propsStateTheme.theme === 'light';

    const { abrirModalGrupo, isGrupoOpen } = useGrupoContext();
    const { grupos, getGrupos, loading: loadingGrupos } = grupoHook;

    const [detalhe, setDetalhe] = useState<IReceitaDetalhe | null>(null);
    const [loadingDetalhe, setLoadingDetalhe] = useState<boolean>(false);
    const [atribuicoesMap, setAtribuicoesMap] = useState<Record<string, string>>({});
    const [grupoMestreId, setGrupoMestreId] = useState<string | null>(null);

    useEffect(() => {
        if (visible) {
            getGrupos();
        }
    }, [visible, getGrupos]);

    useEffect(() => {
        if (!isGrupoOpen && visible) {
            getGrupos();
        }
    }, [isGrupoOpen, visible, getGrupos]);

    useEffect(() => {
        if (visible && data) {
            const codigo = data.codigo_mestre || data.codigo;
            if (codigo && getReceitaByCodigo) {
                setLoadingDetalhe(true);
                getReceitaByCodigo(codigo)
                    .then((res) => {
                        if (res) {
                            setDetalhe(res);
                            const initialMap: Record<string, string> = {};
                            res.sistemas?.forEach((sis) => {
                                sis.codigos?.forEach((c) => {
                                    if (c.grupo_id) {
                                        const compositeKey = `${sis.sistema}-${c.codigo}`;
                                        initialMap[compositeKey] = c.grupo_id;
                                    }
                                });
                            });
                            setAtribuicoesMap(initialMap);
                        } else {
                            setDetalhe(null);
                        }
                    })
                    .finally(() => {
                        setLoadingDetalhe(false);
                    });
            }
        } else {
            setDetalhe(null);
            setAtribuicoesMap({});
            setGrupoMestreId(null);
        }
    }, [visible, data, getReceitaByCodigo]);

    const handleAplicarMestre = () => {
        if (!grupoMestreId || !detalhe) return;
        const newMap: Record<string, string> = { ...atribuicoesMap };
        detalhe.sistemas?.forEach((sis) => {
            sis.codigos?.forEach((c) => {
                const compositeKey = `${sis.sistema}-${c.codigo}`;
                newMap[compositeKey] = grupoMestreId;
            });
        });
        setAtribuicoesMap(newMap);
    };

    const handleSelectGrupoItem = (sistema: string, codigo: string, grupoId: string | null) => {
        setAtribuicoesMap((prev) => {
            const next = { ...prev };
            const compositeKey = `${sistema}-${codigo}`;
            if (grupoId) {
                next[compositeKey] = grupoId;
            } else {
                delete next[compositeKey];
            }
            return next;
        });
    };

    const handleSubmit = () => {
        const atribuicoes: IAtribuicaoItem[] = [];
        detalhe?.sistemas?.forEach((sis) => {
            sis.codigos?.forEach((c) => {
                const compositeKey = `${sis.sistema}-${c.codigo}`;
                const gId = atribuicoesMap[compositeKey];
                if (gId) {
                    atribuicoes.push({
                        sistema: sis.sistema,
                        codigo: c.codigo,
                        grupo_id: gId,
                    });
                }
            });
        });
        onSave({ atribuicoes });
    };

    const totalCodigos = detalhe?.sistemas?.reduce((acc, sis) => acc + (sis.codigos?.length || 0), 0) || 0;

    const codigosComGrupo = detalhe?.sistemas?.reduce((acc, sis) => { return acc + (sis.codigos?.filter((c) => !!atribuicoesMap[`${sis.sistema}-${c.codigo}`]).length || 0); }, 0) || 0;

    const currentStatus = detalhe?.status || data?.status || 'nao_resolvido';
    const statusInfo = STATUS_TAG_COLOR[currentStatus] || { label: 'Não Resolvido', color: 'red' };
    const codigoExibicao = detalhe?.codigo_mestre || data?.codigo_mestre || data?.codigo || '';
    const tipoDescricaoExibicao = detalhe?.tipo_descricao || data?.tipo_descricao || '';
    const dataAtualizacao = detalhe?.atualizado_em || data?.atualizado_em;

    return (
        <Modal
            forceRender
            title={
                <div>
                    <Row align="middle" justify="space-between" style={{ paddingRight: 24, marginBottom: 6 }}>
                        <Col>
                            <span style={{ fontWeight: 600, fontSize: 18 }}>
                                Resolver Receita{' '}
                                <span style={{ color: '#16a34a', fontWeight: 700 }}>
                                    {codigoExibicao}
                                </span>
                            </span>
                        </Col>
                        <Col>
                            <Tag
                                color={statusInfo.color}
                                style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    borderRadius: 4,
                                    padding: '2px 8px',
                                }}
                            >
                                {statusInfo.label}
                            </Tag>
                        </Col>
                    </Row>
                    <Row align="middle" gutter={[12, 4]}>
                        {tipoDescricaoExibicao && (
                            <Col>
                                <Tag
                                    style={{
                                        fontSize: 13,
                                        backgroundColor: isLight ? '#f3f4f6' : '#262626',
                                        borderColor: isLight ? '#e5e7eb' : '#404040',
                                        color: isLight ? '#374151' : '#d1d5db',
                                        borderRadius: 4,
                                        padding: '2px 8px',
                                    }}
                                >
                                    Tipo da Receita: {tipoDescricaoExibicao}
                                </Tag>
                            </Col>
                        )}
                        {dataAtualizacao && (
                            <Col>
                                <Text style={{ fontSize: 13, color: '#6b7280' }}>
                                    Atualizado em {convertDataUSParaBrasilEHora(dataAtualizacao)}
                                </Text>
                            </Col>
                        )}
                    </Row>
                </div>
            }
            width={'calc(100vw - 40%)'}
            open={visible}
            onCancel={onClose}
            centered
            footer={
                <Row align="middle" justify="space-between">
                    <Col>
                        <Text style={{ fontWeight: 700, fontSize: 14 }}>
                            {codigosComGrupo} de {totalCodigos} códigos com grupo definido
                        </Text>
                    </Col>
                    <Col style={{ display: 'flex', gap: 8 }}>
                        <Button key="back" onClick={onClose}>
                            Fechar
                        </Button>
                        <Button
                            key="submit"
                            type="primary"
                            loading={loading}
                            disabled={loading || loadingDetalhe}
                            style={{
                                backgroundColor: '#16a34a',
                                borderColor: '#16a34a',
                                color: '#fff',
                            }}
                            onClick={handleSubmit}
                        >
                            Atualizar
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Spin spinning={loading || loadingDetalhe}>
                <div
                    style={{
                        backgroundColor: isLight ? '#f0fdf4' : '#052e1628',
                        border: isLight ? '1px solid #14532d' : '1px solid #14532d',
                        borderRadius: 12,
                        padding: '16px 20px',
                        marginBottom: 20,
                    }}
                >
                    <Row
                        gutter={[16, 16]}
                        justify="space-between"
                        align="middle"
                    >
                    <Col xs={24} md={13}>
                        <Title
                            style={{
                                color: isLight ? '#14532d' : '#86efac',
                                fontSize: 16,
                                fontWeight: 700,
                                marginBottom: 4,
                                marginTop: 0,
                            }}
                        >
                            Grupo mestre
                        </Title>
                        <Text
                            style={{
                                color: isLight ? '#3f6212' : '#bbf7d0',
                                fontSize: 14,
                            }}
                        >
                            Selecione um grupo e aplique a todos os códigos deste modal de uma vez.
                        </Text>
                    </Col>
                    <Col xs={24} md={11}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 8,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Select
                                showSearch
                                allowClear
                                loading={loadingGrupos}
                                disabled={loadingGrupos}
                                placeholder="Selecione um grupo..."
                                value={grupoMestreId || undefined}
                                onChange={(val) => setGrupoMestreId(val || null)}
                                style={{ minWidth: 220, flex: 1 }}
                                className={isLight ? "select-custom-borda" : "select-custom-borda-dark"}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                dropdownRender={(menu) => (
                                    <div>
                                        <div style={{ padding: 8 }}>
                                            <Button
                                                type="dashed"
                                                block
                                                onClick={() => abrirModalGrupo('grupo')}
                                            >
                                                Novo grupo +
                                            </Button>
                                        </div>
                                        <Divider style={{ margin: '4px 0' }} />
                                        {menu}
                                    </div>
                                )}
                                options={grupos.map((g) => ({
                                    label: g.tipo === null ? g.nome : `${g.tipo} - ${g.nome}`,
                                    value: g.id,
                                }))}
                            />

                            <Button
                                type="primary"
                                disabled={!grupoMestreId}
                                onClick={handleAplicarMestre}
                                style={{
                                    backgroundColor: isLight ? '#14532d' : '#15803d',
                                    borderColor: isLight ? '#14532d' : '#15803d',
                                    color: '#fff',
                                    opacity: !grupoMestreId ? 0.6 : 1,
                                }}
                            >
                                Aplicar a todos
                            </Button>
                        </div>
                    </Col>
                </Row>
                </div>
                <div
                    style={{
                        marginTop: 8,
                        maxHeight: 'calc(80vh - 180px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    {detalhe?.sistemas && detalhe.sistemas.length > 0 ? (detalhe.sistemas.map((si, index) => (
                        <div key={si.sistema + index} style={{ marginBottom: 24 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 12,
                                    width: '100%',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        color: isLight ? '#1f2937' : '#f3f4f6',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                    }}
                                >
                                    {si.label || si.sistema}
                                </Text>
                                <Tag
                                    color="blue"
                                    style={{
                                        borderRadius: 12,
                                        fontWeight: 600,
                                        fontSize: 12,
                                        marginRight: 0,
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                    }}
                                >
                                    {si.codigos?.length || 0} código{si.codigos?.length === 1 ? '' : 's'}
                                </Tag>
                                <Divider style={{ flex: 1, minWidth: 0, margin: 0 }} />
                            </div>
                            {si.codigos?.map((e, index) => (
                                <ItemSI
                                    key={e.codigo + index}
                                    item={e}
                                    sistema={si.sistema}
                                    grupos={grupos}
                                    loadingGrupos={loadingGrupos}
                                    selectedGrupoId={atribuicoesMap[`${si.sistema}-${e.codigo}`] || null}
                                    onSelectGrupo={handleSelectGrupoItem}
                                    abrirModalGrupo={abrirModalGrupo}
                                    theme={propsStateTheme.theme}
                                />
                            ))}
                        </div>
                    ))
                    ) : (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: 20
                            }}
                        >
                            <Text type="secondary">
                                {loadingDetalhe ? 'Carregando dados da receita...' : 'Nenhum sistema ou código encontrado.'}
                            </Text>
                        </div>
                    )}
                </div>
            </Spin>
        </Modal>
    );
};
